import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { prefixFilter } from '@utils/filter.utils'

import { TransactionDto } from '@model/TransactionDto'
import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

export interface WalletWithLatestTransactions {
  wallet: WalletDto
  latestTransactions: TransactionDto[]
}

interface UseWalletsWithLatestTransactionsValue {
  walletsWithLatestTransactions: WalletWithLatestTransactions[]
  refreshWalletsWithLatestTransactions: (skipLoadingFlag?: boolean) => void
  isLoading: boolean
}

export const useWalletsWithLatestTransactions = (): UseWalletsWithLatestTransactionsValue => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()
  const locale = useSessionLanguage()

  const { data: walletsWithLatestTransactions = [], isLoading } = useQuery({
    queryKey: queryKeys.wallet.withTransactions(account?.id, locale),
    queryFn: async () => {
      const wallets = await apiClient.getWallets({
        walletTypeId: 10,
        pageSize: 2,
        pageIndex: 1,
      })

      const transactions = await Promise.all(
        wallets.map((wallet) => {
          return apiClient.getTransactions({
            ca_search_WalletId: wallet.id,
            ca_search_ClientWalletType: prefixFilter({
              prefix: account?.id,
              value: WalletTypeEnum.IB.toString(),
            }),
            caller: 'ca',
            pageSize: 3,
            pageIndex: 1,
            languageId: locale,
          })
        })
      )

      return wallets.map<WalletWithLatestTransactions>((wallet, index) => {
        return {
          wallet: wallet,
          latestTransactions: transactions[index].items || [],
        }
      })
    },
    enabled: !!account,
    refetchOnMount: 'always',
  })

  const refreshWalletsWithLatestTransactions = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.wallet.withTransactions(account?.id, locale),
    })
  }, [queryClient, account?.id, locale])

  return {
    walletsWithLatestTransactions,
    isLoading,
    refreshWalletsWithLatestTransactions,
  }
}
